/**
 * src/pages/ClientProfile.jsx — one client's whole history on a page.
 *
 * The API returns projects, appointments and media in a single response (see
 * GET /api/clients/:id), so this page renders in one round trip rather than
 * four sequential fetches from the browser.
 *
 * Notes are editable in place because that is the field an artist actually
 * changes — "reacts badly to red", "prefers mornings" — and making them open a
 * dialog to do it would mean they simply would not.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useApi } from '../lib/useApi.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { Empty, ErrorBanner, Loader, PageHeader, Pill } from '../components/ui.jsx';

export default function ClientProfile() {
  const { id } = useParams();
  const { timezone } = useAuth();
  const { data, error, loading, reload } = useApi(`/api/clients/${id}`);

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const client = data?.client;

  const saveNotes = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.patch(`/api/clients/${id}`, { notes: draft });
      setDraft(null);
      reload();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Loader loading={loading} error={error}>
      {client ? (
        <>
          <PageHeader
            title={client.name}
            subtitle={`${client.email}${client.phone ? ` · ${client.phone}` : ''} · client since ${formatDate(client.createdAt, timezone)}`}
            actions={<Link to="/clients" className="btn">Back to clients</Link>}
          />

          <div className="stack-lg">
            <section className="card">
              <h2 style={{ marginBottom: 10 }}>Notes</h2>
              <ErrorBanner>{saveError}</ErrorBanner>
              <textarea
                value={draft ?? client.notes ?? ''}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Allergies, skin type, how they sat last time…"
              />
              <div className="row" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={saving || draft === null}
                  onClick={saveNotes}
                >
                  {saving ? 'Saving…' : 'Save notes'}
                </button>
                {draft !== null ? (
                  <button type="button" className="btn btn-sm" onClick={() => setDraft(null)}>
                    Discard
                  </button>
                ) : null}
              </div>
            </section>

            <section className="card">
              <h2 style={{ marginBottom: 14 }}>Projects</h2>
              {(data.projects ?? []).length === 0 ? (
                <Empty>No multi-session projects for this client.</Empty>
              ) : (
                <div className="stack">
                  {data.projects.map((p) => (
                    <div key={p.id} className="row-between">
                      <div>
                        <Link to={`/projects/${p.id}`}><strong>{p.title}</strong></Link>
                        <div className="muted small">
                          {p.artist_name}
                          {p.body_location ? ` · ${p.body_location}` : ''}
                        </div>
                      </div>
                      <Pill status={p.status} />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="card">
              <h2 style={{ marginBottom: 14 }}>Appointments</h2>
              {(data.appointments ?? []).length === 0 ? (
                <Empty>Nothing booked yet.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Artist</th>
                        <th>Status</th>
                        <th>Deposit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.appointments.map((a) => (
                        <tr key={a.id}>
                          <td className="mono">{formatDateTime(a.starts_at, timezone)}</td>
                          <td className="muted">{a.artist_name}</td>
                          <td><Pill status={a.status} /></td>
                          <td className="muted">{a.deposit_paid ? 'Paid' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="card">
              <h2 style={{ marginBottom: 14 }}>Photos</h2>
              {(data.media ?? []).length === 0 ? (
                <Empty>No reference or healed photos yet.</Empty>
              ) : (
                <div className="gallery">
                  {data.media.map((m) => (
                    <figure key={m.id} style={{ margin: 0 }}>
                      <img src={m.url} alt={m.asset_type.replace(/_/g, ' ')} loading="lazy" />
                      <figcaption className="muted small" style={{ marginTop: 4 }}>
                        {m.asset_type.replace(/_/g, ' ')} · {formatDate(m.created_at, timezone)}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </section>
          </div>
        </>
      ) : null}
    </Loader>
  );
}
